import * as React from "react";

import { Alert } from "Components/ui";
import { Lock, TriangleAlert } from "Components/ui/icons";
import { BillingState } from "api/billing";
import { TenantLinkStatus } from "api/teleopdLink";
import { cn } from "utils/cn";
import { formatDate, formatDateTime, formatINR } from "utils/format";

interface TeleopdPreviewProps {
  state: BillingState;
  audienceNames: string[];
  link: TenantLinkStatus | undefined;
}

const MockWindow: React.FC<{ label: string; children?: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="overflow-hidden rounded-card border border-border bg-surface">
    <div className="flex items-center gap-1.5 border-b border-border bg-surface-muted/60 px-3 py-1.5">
      <span className="h-2 w-2 rounded-full bg-danger/40" aria-hidden="true" />
      <span className="h-2 w-2 rounded-full bg-warning/40" aria-hidden="true" />
      <span className="h-2 w-2 rounded-full bg-success/40" aria-hidden="true" />
      <span className="ml-2 truncate text-xs font-medium text-muted-foreground">{label}</span>
    </div>
    <div className="space-y-2 p-3">
      {children}
      <div className="space-y-1.5" aria-hidden="true">
        <div className="h-2 w-3/4 rounded-full bg-surface-muted" />
        <div className="h-2 w-1/2 rounded-full bg-surface-muted" />
      </div>
    </div>
  </div>
);

const MOCK_BUTTON_TONES = {
  warning: "bg-warning/20 text-warning-text",
  outline: "border border-warning/40 bg-surface text-warning-text",
  danger: "border border-danger/30 bg-surface text-danger-text",
};

const MockButton: React.FC<{
  tone: keyof typeof MOCK_BUTTON_TONES;
  children?: React.ReactNode;
}> = ({ tone, children }) => (
  <span
    className={cn(
      "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold",
      MOCK_BUTTON_TONES[tone]
    )}
  >
    {children}
  </span>
);

const NothingShown: React.FC = () => (
  <p className="rounded-control border border-dashed border-border px-3 py-2 text-xs text-subtle-foreground">
    Nothing shown
  </p>
);

/** Mock of the TeleOpd SubscriptionBanner for the current SAP-side state. */
const TeleopdPreview: React.FC<TeleopdPreviewProps> = ({ state, audienceNames, link }) => {
  // Enabled and synced at least once (services/teleopdLink/linkStatus.js isConnected).
  const connected = Boolean(link && link.connected);
  const canLock = Boolean(link && link.readOnlyCapable);
  const notice = state.notice;
  const effectiveMode = state.mode === "read_only" && !canLock ? "warning" : state.mode;
  const amount = formatINR(notice ? notice.amountDue : state.amountDue);
  const lockText =
    state.enforcement === "read_only" && state.lockAt
      ? ` · read-only from ${formatDateTime(state.lockAt)}`
      : "";

  const warningBanner = (
    <div className="rounded-control border border-warning/25 bg-warning-subtle px-3 py-2 text-xs text-warning-text">
      <p className="flex items-start gap-1.5 font-semibold">
        <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span>
          {amount} due on {notice ? formatDate(notice.dueDate) : "—"}
          {lockText}
        </span>
      </p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        <MockButton tone="warning">Pay now</MockButton>
        <MockButton tone="outline">Hide today</MockButton>
      </div>
    </div>
  );

  const audiencePanel =
    effectiveMode === "ok" ? (
      <NothingShown />
    ) : effectiveMode === "warning" ? (
      warningBanner
    ) : (
      <div className="rounded-control border border-danger/25 bg-danger-subtle px-3 py-2 text-xs text-danger-text">
        <p className="flex items-start gap-1.5 font-semibold">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            Read-only: {amount} overdue
            {notice ? ` (invoice ${notice.invoiceNumber}, due ${formatDate(notice.dueDate)})` : ""}.
            Pay now to restore full access.
          </span>
        </p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          <MockButton tone="danger">Pay now</MockButton>
          <MockButton tone="danger">I've paid — refresh</MockButton>
        </div>
      </div>
    );

  const othersPanel =
    effectiveMode === "read_only" ? (
      <div className="rounded-control border border-danger/25 bg-danger-subtle px-3 py-2 text-xs font-semibold text-danger-text">
        <p className="flex items-start gap-1.5">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          TeleOpd is read-only. Contact your administrator.
        </p>
      </div>
    ) : (
      <NothingShown />
    );

  return (
    <div className="space-y-3">
      {!connected && (
        <Alert tone="neutral">
          TeleOpd is not connected (or the link is disabled), so nothing reaches TeleOpd yet. This
          is what it would show once connected.
        </Alert>
      )}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <MockWindow
          label={
            audienceNames.length > 0
              ? `Billing roles: ${audienceNames.join(", ")}`
              : "Billing roles (none chosen)"
          }
        >
          {audiencePanel}
        </MockWindow>
        <MockWindow label="All other staff">{othersPanel}</MockWindow>
      </div>
      <ul className="space-y-1 text-xs text-muted-foreground">
        {audienceNames.length === 0 && state.mode !== "ok" && (
          <li>
            No roles are chosen to see warnings, so only the read-only banner (if any) reaches
            staff. Pick roles on the Rules tab.
          </li>
        )}
        {state.mode === "read_only" && !canLock && (
          <li>
            TeleOpd can't enforce read-only yet (lock switch {link?.lockSwitch || "unknown"}, or no
            recent sync), so it keeps showing the warning instead.
          </li>
        )}
        {state.mode === "read_only" && canLock && (
          <li>TeleOpd blocks staff writes only after the warning has been visible for 72 hours.</li>
        )}
        {state.paused && <li>Enforcement is paused, so TeleOpd shows nothing until it ends.</li>}
        {state.enforcement === "off" && <li>Enforcement is off: TeleOpd never shows a notice.</li>}
      </ul>
    </div>
  );
};

export default TeleopdPreview;
